'use client';
import { useEffect, useState } from 'react';
import { TOTP_PERIOD_MS } from '../../lib/totpPeriod';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useEffects } from '../../lib/useEffects';

/** One period of the ring, mounted per TOTP step so a new code starts a full ring. The drain itself is a
 *  CSS animation (`.totp-countdown-drain`) started at the part of the period already elapsed on mount. */
function DrainRing({ elapsedMs }: { elapsedMs: number }) {
  const [delay] = useState(() => -elapsedMs);
  return (
    <svg data-slot="totp-countdown-ring" viewBox="0 0 16 16" className="size-4 -rotate-90" aria-hidden>
      <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2.5" className="stroke-border" />
      <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2.5" pathLength={1} strokeDasharray="1"
        className="totp-countdown-drain stroke-foreground"
        style={{ animationDuration: `${TOTP_PERIOD_MS}ms`, animationDelay: `${delay}ms` }} />
    </svg>
  );
}

/** How long the authenticator's current code stays valid, from the client clock against the Unix-epoch
 *  TOTP step the daemon verifies with. One timer ticks on whole seconds; quiet effects show the number only. */
export function TotpCountdown() {
  const { t } = useTranslation();
  const { ambientMotionEnabled } = useEffects();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let timer = 0;
    const schedule = () => { timer = window.setTimeout(tick, 1000 - (Date.now() % 1000)); };
    const tick = () => { setNow(Date.now()); schedule(); };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);
  const elapsedMs = now % TOTP_PERIOD_MS;
  const seconds = Math.ceil((TOTP_PERIOD_MS - elapsedMs) / 1000);
  return (
    <span data-slot="totp-countdown" className="inline-flex items-center gap-1.5 text-meta tabular-nums text-muted-foreground">
      {ambientMotionEnabled ? <DrainRing key={Math.floor(now / TOTP_PERIOD_MS)} elapsedMs={elapsedMs} /> : null}
      <span aria-hidden>{interpolate(t.totp.codeSecondsLeft, { seconds })}</span>
      <span className="sr-only">{interpolate(t.totp.codeValidFor, { seconds })}</span>
    </span>
  );
}
